import moment from "moment";
import { LoadState } from "../../constants/enums";
import { getUniqueId } from "../../helpers";
import { STANDARD_APP_DATE_FORMAT, STANDARD_APP_TIME_FORMAT } from "../../constants/constants";

/**
 * Payment-schedule item shape shared by the merged customer-policy-details record.
 * The app's existing premium grids bind to the `installment_*` shape, which is also
 * what the merged fetch response returns, so we keep it as the canonical shape.
 */
export interface ICustomerPolicyDetailsPaymentSchedule {
  id: string;
  installment_number: string;
  installment_amount: string;
  installment_date: string;
}

/**
 * The single shared interface describing both the Merged_Save_Endpoint request
 * payload and a single Merged_Fetch_Endpoint response record. All five policy
 * types (Automobile, Commercial Automobile, Business, Habitational, Life Insurance)
 * import this interface and read/write the merged contract through the canonical
 * field names below.
 *
 * Fields are nullable where the persisted record allows, consistent with the
 * existing per-section types. Life-only fields are nullable and are `null` for
 * non-life policy types.
 */
export interface ICustomerPolicyDetails {
  // Identity / classification
  customer_policy_id: number | null;
  customer_id: number;
  policy_id: number;
  line_of_business: string | null;
  policy_sub_type: string | null;
  customer_name: string | null;
  customer_code: string | null;
  form_of_business: string | number | null;
  language: string | null;
  transaction_type: string | null;

  // Address
  residential_or_business: string | null;
  address: string | null;
  unit_or_suite: string | null;
  city: string | null;
  province_or_state: string | null;
  postal_code: string | null;
  country: string | null;

  // Policy lifecycle
  effective_date: string;
  effective_time: string;
  expiry_date: string;
  binder_number: string | null;
  quote_number: string | null;
  policy_number: string | null;
  policy_status: string | null;
  current_policy_status: string | null;
  date_cancelled: string | null;
  cancellation_reason: string | null;
  cancellation_type: string | null;
  program: string | null;
  type_of_policy: string | null;
  remarketed: string | null;
  segment: string[];
  status: string;

  // Admin
  insurer: string | null;
  broker_code: string | null;
  broker_sub_code: string | null;
  branch_code: string | null;
  commission_code: string | null;
  commission_percentage: string | null;
  commission_code_percentage: string | null;
  policy_fee: string | null;
  marketer: string | null;
  marketer_code: string | null;
  csr: string | null;
  csr_code: string | null;
  billing_account_number: string | null;

  // Producers
  producer_one: string | null;
  producer_one_code: string | null;
  producer_two: string | null;
  producer_two_code: string | null;
  producer_three: string | null;
  producer_three_code: string | null;
  previous_producer_code: string | null;

  // Producer percentages
  producer_one_new_percentage: string | null;
  producer_one_renewal_percentage: string | null;
  producer_one_endorsement_percentage: string | null;
  producer_two_new_percentage: string | null;
  producer_two_renewal_percentage: string | null;
  producer_two_endorsement_percentage: string | null;
  producer_three_new_percentage: string | null;
  producer_three_renewal_percentage: string | null;
  producer_three_endorsement_percentage: string | null;

  // House percentages
  house_new_percentage: string | null;
  house_renewal_percentage: string | null;
  house_endorsement_percentage: string | null;

  // Branch percentages
  branch_new_percentage: string | null;
  branch_renewal_percentage: string | null;
  branch_endorsement_percentage: string | null;

  // Premium
  premium: string | null;
  premium_financed: string | null;
  premium_finance_contract_number: string | null;
  financed_by: string | null;
  agency_bill_or_direct_bill: string | null;
  payment_plan: string | null;
  payment_schedule: ICustomerPolicyDetailsPaymentSchedule[];
  default_tax_region: string | null;
  tax_one: string | null;
  tax_two: string | null;
  tax_exempt: string | null;
  tax_exempt_reason: string | null;

  // Email flags
  send_statements_via_email: boolean | null;
  send_invoices_renewal_receivable_via_email: boolean | null;
  send_edocs_via_email: boolean | null;

  // Life-only fields (nullable, null for non-life policy types)
  aga: string | null;
  fyc: string | null;
  bonus_percent: string | null;
  bonus_amount: string | null;
  policy_limit: string | null;
  policy_type: string | null;
}

/**
 * The Merged_Fetch_Endpoint response wrapper. `data` is an array of merged
 * customer-policy-details records; the first record is the current record.
 */
export interface ICustomerPolicyDetailsResponse {
  message: string;
  currentRecords: number;
  data: ICustomerPolicyDetails[];
}

/**
 * Shared slice state for the merged customer-policy-details contract.
 */
export interface ICustomerPolicyDetailsState {
  details: {
    data: ICustomerPolicyDetails;
    loading: LoadState;
    error: string | null;
  };
  customerPolicyId?: number | null;
  isNewPolicy: boolean;
}

const defaultPaymentSchedule = (): ICustomerPolicyDetailsPaymentSchedule[] =>
  Array.from({ length: 12 }, () => ({
    id: getUniqueId(),
    installment_number: "",
    installment_amount: "",
    installment_date: "",
  }));

export const initialCustomerPolicyDetails: ICustomerPolicyDetailsState = {
  details: {
    data: {
      // Identity / classification
      customer_policy_id: null,
      customer_id: 0,
      policy_id: 0,
      line_of_business: null,
      policy_sub_type: null,
      customer_name: "",
      customer_code: null,
      form_of_business: null,
      language: null,
      transaction_type: null,

      // Address
      residential_or_business: null,
      address: "",
      unit_or_suite: null,
      city: "",
      province_or_state: "",
      postal_code: "",
      country: "",

      // Policy lifecycle
      effective_date: moment().format(STANDARD_APP_DATE_FORMAT),
      effective_time: moment().startOf("day").add(1, "minute").format(STANDARD_APP_TIME_FORMAT),
      expiry_date: moment().add(1, "year").format(STANDARD_APP_DATE_FORMAT),
      binder_number: null,
      quote_number: null,
      policy_number: null,
      policy_status: "",
      current_policy_status: "Prospect",
      date_cancelled: null,
      cancellation_reason: null,
      cancellation_type: null,
      program: null,
      type_of_policy: null,
      remarketed: null,
      segment: [],
      status: "ACTIVE",

      // Admin
      insurer: null,
      broker_code: null,
      broker_sub_code: null,
      branch_code: null,
      commission_code: null,
      commission_percentage: null,
      commission_code_percentage: null,
      policy_fee: null,
      marketer: null,
      marketer_code: null,
      csr: null,
      csr_code: null,
      billing_account_number: null,

      // Producers
      producer_one: null,
      producer_one_code: null,
      producer_two: null,
      producer_two_code: null,
      producer_three: null,
      producer_three_code: null,
      previous_producer_code: null,

      // Producer percentages
      producer_one_new_percentage: null,
      producer_one_renewal_percentage: null,
      producer_one_endorsement_percentage: null,
      producer_two_new_percentage: null,
      producer_two_renewal_percentage: null,
      producer_two_endorsement_percentage: null,
      producer_three_new_percentage: null,
      producer_three_renewal_percentage: null,
      producer_three_endorsement_percentage: null,

      // House percentages
      house_new_percentage: null,
      house_renewal_percentage: null,
      house_endorsement_percentage: null,

      // Branch percentages
      branch_new_percentage: null,
      branch_renewal_percentage: null,
      branch_endorsement_percentage: null,

      // Premium
      premium: null,
      premium_financed: null,
      premium_finance_contract_number: null,
      financed_by: null,
      agency_bill_or_direct_bill: null,
      payment_plan: null,
      payment_schedule: defaultPaymentSchedule(),
      default_tax_region: "ON",
      tax_one: null,
      tax_two: null,
      tax_exempt: null,
      tax_exempt_reason: null,

      // Email flags
      send_statements_via_email: true,
      send_invoices_renewal_receivable_via_email: true,
      send_edocs_via_email: true,

      // Life-only fields default to null for non-life policy types
      aga: null,
      fyc: null,
      bonus_percent: null,
      bonus_amount: null,
      policy_limit: null,
      policy_type: null,
    },
    loading: LoadState.NotLoaded,
    error: null,
  },
  customerPolicyId: null,
  isNewPolicy: true,
};
